import { asArray, asOptionalString, asRecord, asString, reportDtoAnomaly } from './common';
import type { OrderSalesInvoice, OrderSalesInvoiceState } from '../types/orderSalesInvoice';
import type { AdvanceInvoiceBalance } from '../modules/invoices/api/invoiceWorkflowApi';

const STATES: readonly string[] = ['DRAFT', 'APPROVED', 'WITHDRAWN', 'OTHER'];

function amount(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}

/** `invoices` of GET /invoices/order/:orderId/documents; an unexpected row is reported and skipped. */
export function normalizeOrderSalesInvoices(raw: unknown): OrderSalesInvoice[] {
  const record = asRecord(raw);
  if (!record || !Array.isArray(record.invoices)) {
    reportDtoAnomaly('orderSalesInvoices', 'invoices', raw);
    return [];
  }
  return asArray(record.invoices, (item): OrderSalesInvoice | null => {
    const row = asRecord(item);
    const state = asString(row?.state);
    if (!row?.id || !STATES.includes(state)) {
      reportDtoAnomaly('orderSalesInvoices', 'invoice', item);
      return null;
    }
    const withdrawal = asRecord(row.withdrawal);
    return {
      id: asString(row.id),
      number: asOptionalString(row.number) ?? null,
      documentKind: asOptionalString(row.documentKind) ?? 'UNKNOWN',
      state: state as OrderSalesInvoiceState,
      issueDate: asOptionalString(row.issueDate) ?? null,
      recognitionMonth: asOptionalString(row.recognitionMonth) ?? null,
      corrected: row.corrected === true,
      correctionNumber: asOptionalString(row.correctionNumber) ?? null,
      currency: asOptionalString(row.currency) ?? null,
      netAmount: amount(row.netAmount),
      grossAmount: amount(row.grossAmount),
      withdrawal: withdrawal ? {
        recognitionMonth: asString(withdrawal.recognitionMonth),
        reason: asString(withdrawal.reason),
        withdrawnAt: asString(withdrawal.withdrawnAt),
      } : null,
      canDelete: row.canDelete === true,
      canWithdraw: row.canWithdraw === true,
      withdrawalMonthSuggestion: typeof row.withdrawalMonthSuggestion === 'string' ? row.withdrawalMonthSuggestion : null,
      deleteBlockedReason: row.deleteBlockedReason === 'COMPLAINT' ? 'COMPLAINT' : null,
    };
  }).filter((row): row is OrderSalesInvoice => row !== null);
}

/** `advanceBalance` of the same response: null when the ZK has no advance invoice. */
export function normalizeOrderAdvanceBalance(raw: unknown): AdvanceInvoiceBalance | null {
  const balance = asRecord(asRecord(raw)?.advanceBalance);
  if (!balance) return null;
  if (!Array.isArray(balance.documents) || typeof balance.invoicedNetPLN !== 'number') {
    reportDtoAnomaly('orderSalesInvoices', 'advanceBalance', balance);
    return null;
  }
  return balance as unknown as AdvanceInvoiceBalance;
}
